import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Verification progress indicator.
 * Shows a percentage bar based on how many docs are approved out of the required total.
 */
export default function VerificationProgress({
  badges,
  forHost = false,
  className = "",
}) {
  const { t } = useTranslation();
  if (!badges) return null;

  const required = forHost
    ? [
        { key: "id_verified", label: t("verifProgress.identity") },
        { key: "license_verified", label: t("verifProgress.license") },
        { key: "selfie_verified", label: t("verifProgress.selfie") },
        { key: "address_verified", label: t("verifProgress.address") },
        { key: "vehicle_ownership_verified", label: t("verifProgress.ownership") },
      ]
    : [
        { key: "id_verified", label: t("verifProgress.identity") },
        { key: "license_verified", label: t("verifProgress.license") },
        { key: "selfie_verified", label: t("verifProgress.selfie") },
      ];

  const done = required.filter((r) => badges[r.key]).length;
  const total = required.length;
  const pct = Math.round((done / total) * 100);
  const complete = pct === 100;

  return (
    <div
      className={`p-4 rounded-2xl border ${
        complete
          ? "bg-emerald-50 border-emerald-200"
          : "bg-neutral-50 border-neutral-200"
      } ${className}`}
      data-testid="verification-progress"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck
            className={`w-4 h-4 ${complete ? "text-emerald-600" : "text-neutral-500"}`}
          />
          <span className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
            {forHost ? t("verifProgress.hostTitle") : t("verifProgress.idTitle")}
          </span>
        </div>
        <span
          className={`text-sm font-heading font-bold tabular-nums ${
            complete ? "text-emerald-700" : "text-[#111111]"
          }`}
        >
          {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/60 overflow-hidden border border-neutral-200">
        <div
          className={`h-full transition-all duration-500 ${
            complete ? "bg-emerald-500" : "bg-terracotta"
          }`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <p className="text-xs text-neutral-600 mt-2">
        {t("verifProgress.stepsDone", { done, total })}
      </p>
    </div>
  );
}
